import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getProductDetail, incrementProductView } from '../services/api'

const ProductDetail = () => {
  const { id } = useParams()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [showContact, setShowContact] = useState(false)
  const [contactImageZoomed, setContactImageZoomed] = useState(false)
  const [activeImage, setActiveImage] = useState(null) // 当前显示的主图，null表示默认主图

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true)
        const productData = await getProductDetail(id)
        
        if (productData) {
          // api.js已经返回了前端页面期望的格式，直接使用
          setProduct(productData)
          
          // 增加产品访问计数，并更新显示的计数
          try {
            const viewResult = await incrementProductView(id)
            if (viewResult && viewResult.view_count !== undefined) {
              setProduct(prev => ({ ...prev, viewCount: viewResult.view_count }))
            }
          } catch (viewError) {
            console.error('增加访问计数失败:', viewError)
            // 静默失败，不影响用户体验
          }
        } else {
          setProduct(null)
        }
      } catch (error) {
        console.error('加载产品详情失败:', error)
        setProduct(null)
      } finally {
        setLoading(false)
      }
    }
    
    if (id) {
      loadProduct()
    }
  }, [id])

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-gray-50">
        <div className="container-max section-padding">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="h-96 bg-gray-300 rounded"></div>
              <div className="space-y-4">
                <div className="h-6 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="pt-20 min-h-screen bg-gray-50">
        <div className="container-max section-padding text-center">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-tech mb-2">产品未找到</h2>
          <p className="text-gray-600 mb-6">您查找的产品不存在或已被移除</p>
          <Link to="/products" className="btn-primary">
            返回产品列表
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-20 min-h-screen bg-gray-50">
      <div className="container-max section-padding">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm text-gray-600">
            <li>
              <Link to="/" className="hover:text-primary transition-colors">首页</Link>
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </li>
            <li>
              <Link to="/products" className="hover:text-primary transition-colors">产品介绍</Link>
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </li>
            <li className="text-primary font-medium">{product.name}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-4">
              <img
                src={activeImage || product.image}
                alt={product.name}
                className="w-full h-96 object-contain"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              {/* 图库缩略图 */}
              {product.gallery.map((image, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-lg shadow-sm overflow-hidden cursor-pointer border-2 transition-all ${
                    (activeImage || product.image) === image ? 'border-primary ring-2 ring-primary/30' : 'border-transparent hover:border-gray-300'
                  }`}
                  onClick={() => setActiveImage(image)}
                >
                  <img
                    src={image}
                    alt={`${product.name} ${index + 1}`}
                    className="w-full h-24 object-cover"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="mb-4">
                <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
                  {product.category === 'hardware' ? '硬件产品' : 
                   product.category === 'software' ? '软件产品' : '解决方案'}
                </span>
              </div>
              
              <h1 className="text-3xl font-bold text-gray-tech mb-4">
                {product.name}
              </h1>
              
              <p className="text-gray-600 text-lg mb-6">
                {product.description}
              </p>

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-tech mb-3">主要特性</h3>
                <div 
                  className="text-gray-600 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: product.features || '' }}
                />
              </div>

              <div className="flex items-center justify-between mb-6">
                <div>
                  <span className="text-2xl font-bold text-primary">{product.priceInfo}</span>
                </div>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setShowContact(!showContact)
                    setContactImageZoomed(false)
                  }}
                >
                  联系我们
                </button>
              </div>

              {/* 联系方式图片 - 点击联系我们按钮后显示 */}
              {showContact && product.contactImage && (
                <div className={`flex mb-4 transition-all duration-300 ${contactImageZoomed ? 'justify-center' : 'justify-end'}`}>
                  <div className="relative inline-block">
                    <img
                      src={product.contactImage}
                      alt="联系方式（点击查看原图大小）"
                      className={`rounded shadow-md cursor-pointer transition-all duration-300 ease-in-out ${
                        contactImageZoomed 
                          ? 'shadow-2xl ring-4 ring-primary/20' 
                          : 'hover:shadow-lg hover:scale-110'
                      }`}
                      style={contactImageZoomed 
                        ? { width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }
                        : { width: '30px', height: '30px', objectFit: 'contain' }
                      }
                      onClick={() => setContactImageZoomed(!contactImageZoomed)}
                    />
                    {contactImageZoomed && (
                      <button
                        className="absolute -top-2 -right-2 w-6 h-6 bg-gray-800 text-white rounded-full text-xs flex items-center justify-center hover:bg-gray-700 transition-colors shadow-md"
                        onClick={() => setContactImageZoomed(false)}
                        title="缩小"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* 访问计数器 */}
              <div className="flex items-center justify-end mb-4">
                <div className="flex items-center text-gray-800" style={{ fontSize: '16px' }}>
                  <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span>{product.viewCount || 0} 次访问</span>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-gray-500">
                  最后更新: {new Date(product.updatedAt).toLocaleDateString('zh-CN')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className="mt-12">
          <div className="bg-white rounded-lg shadow-md">
            {/* Tab Navigation */}
            <div className="border-b">
              <nav className="flex overflow-x-auto">
                {[
                  { id: 'overview', name: '产品概述' },
                  { id: 'specifications', name: '技术规格' },
                  { id: 'applications', name: '应用场景' },
                  { id: 'support', name: '服务支持' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 px-6 py-4 font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'overview' && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-tech mb-4">产品概述</h3>
                  <div 
                    className="text-gray-600 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: product.fullDescription || '' }}
                  />
                </div>
              )}

              {activeTab === 'specifications' && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-tech mb-4">技术规格</h3>
                  <div 
                    className="text-gray-600 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: product.specifications || '' }}
                  />
                </div>
              )}

              {activeTab === 'applications' && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-tech mb-4">应用场景</h3>
                  <div 
                    className="text-gray-600 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: product.applications || '' }}
                  />
                </div>
              )}

              {activeTab === 'support' && (
                <div>
                  <h3 className="text-xl font-semibold text-gray-tech mb-4">服务支持</h3>
                  <div 
                    className="text-gray-600 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: product.supportInfo || '' }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductDetail
